<script lang="ts">
    import { format, fromUnixTime, startOfDay } from 'date-fns';
    import { cn } from '@/lib/utils';
    import AdaptForecastDayCard, { dayKey, isDayChanged as isChanged, isNotableChange } from './AdaptForecastDayCard.svelte';
    import Switch from '@/lib/components/ui/switch/switch.svelte';
    import HelpTooltip from '@/Common/HelpTooltip.svelte';

    // Forecast result days of the current plan and of the adapted forecast, from the first adapted day on.
    export let before: any[]
    export let after: any[]

    $: rows = (() => {
        const byDay = new Map<string, { date: Date, before: any | null, after: any | null }>()
        for (const [side, days] of [['before', before], ['after', after]] as const) {
            for (const day of days ?? []) {
                const key = dayKey(day)
                const row = byDay.get(key) ?? { date: startOfDay(fromUnixTime(day.t)), before: null, after: null }
                row[side] = row[side] ?? day
                byDay.set(key, row)
            }
        }
        return [...byDay.entries()]
            .sort(([a], [b]) => a < b ? -1 : 1)
            .map(([key, row]) => { const changed = isChanged(row.before, row.after); return { key, ...row, changed, notable: changed && isNotableChange(row.before, row.after) } })
    })()

    // Only the days that change are listed; the rest of the plan stays as it is.
    $: changedRows = rows.filter(r => r.changed)
    $: notableRows = changedRows.filter(r => r.notable)
    // Most changes are a few minutes or a couple of XSS, so start with the ones worth a look.
    let notableOnly = true
    $: shownRows = notableOnly ? notableRows : changedRows
</script>

<div class="flex flex-col gap-2 text-left">
    <p class="mb-0 text-sm text-slate-600">
        {#if changedRows.length > 0}
            {changedRows.length} of {rows.length} activities were changed but only {notableRows.length} {notableRows.length === 1 ? 'was' : 'were'} notable. Review them, then Save Forecast to accept the adapted forecast.
        {:else}
            No activities change. Your plan already fits your availability.
        {/if}
    </p>

    {#if changedRows.length > 0}
        <div class="flex items-center gap-1">
            <label class="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                <Switch checked={notableOnly} onCheckedChange={(v) => notableOnly = v} />
                Show Notable Changes Only
            </label>
            <HelpTooltip>
                <div class="text-sm space-y-2 text-left">
                    <p class="mb-0 font-semibold">What is a notable change?</p>
                    <p class="mb-0">A change that alters what you will do, not just a few minutes or a couple of XSS. A day is notable when:</p>
                    <ul class="mb-0 list-disc pl-5 space-y-0.5">
                        <li>a rest day is added or removed</li>
                        <li>a Low Intensity day becomes High Intensity, or the reverse</li>
                        <li>its XSS changes by more than 5%</li>
                    </ul>
                    <p class="mb-0">Turn the switch off to see every changed day, including the minor ones.</p>
                </div>
            </HelpTooltip>
        </div>
    {/if}

    <div class={cn('details-scroll max-h-[55dvh] pr-1', shownRows.length > 0 && 'overflow-y-scroll')}>
        {#if shownRows.length > 0}
            <div class="sticky top-0 z-10 hidden sm:grid grid-cols-[1fr,2rem,1fr] gap-2 border-b bg-white py-1 text-xs font-semibold uppercase text-muted-foreground">
                <span>Current plan</span>
                <span></span>
                <span>Adapted forecast</span>
            </div>
        {/if}

        {#if notableOnly && changedRows.length > 0 && notableRows.length === 0}
            <p class="my-4 text-center text-sm text-slate-600">
                No notable changes. Turn off Show Notable Changes Only to see the {changedRows.length} minor {changedRows.length === 1 ? 'one' : 'ones'}.
            </p>
        {/if}

        {#each shownRows as row (row.key)}
            <section class="my-2 rounded px-2 py-1">
                <h4 class="mb-1 text-sm font-semibold text-slate-700">
                    {format(row.date, 'EEEE, MMMM d')}
                    {#if row.notable}
                        <span class="ml-2 rounded-sm bg-amber-100 px-1.5 text-[10px] font-medium uppercase text-amber-800">Notable</span>
                    {:else}
                        <span class="ml-2 rounded-sm bg-slate-100 px-1.5 text-[10px] font-medium uppercase text-slate-600">Minor</span>
                    {/if}
                </h4>
                <!-- Narrow screens stack the cards: current above adapted. -->
                <div class="grid grid-cols-1 sm:grid-cols-[1fr,2rem,1fr] items-center gap-1 sm:gap-2">
                    <div class="min-w-0">
                        <span class="text-[10px] font-semibold uppercase text-muted-foreground sm:hidden">Current plan</span>
                        <AdaptForecastDayCard day={row.before} />
                    </div>
                    <div class="text-center text-lg text-[--color-xert-teal]">
                        <i class="fa-solid fa-arrow-down sm:hidden"></i>
                        <i class="fa-solid fa-arrow-right hidden sm:inline"></i>
                    </div>
                    <div class="min-w-0">
                        <span class="text-[10px] font-semibold uppercase text-muted-foreground sm:hidden">Adapted forecast</span>
                        <AdaptForecastDayCard day={row.after} />
                    </div>
                </div>
            </section>
        {/each}
    </div>
</div>

<style lang="scss">
    // Always-visible scrollbar that also scrolls by touch on mobile.
    .details-scroll {
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
        touch-action: pan-y;
        scrollbar-width: thin;
        scrollbar-color: #94a3b8 #f1f5f9;

        &::-webkit-scrollbar {
            width: 10px;
        }
        &::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 5px;
        }
        &::-webkit-scrollbar-thumb {
            background: #94a3b8;
            border-radius: 5px;
        }
    }
</style>
